import React, { useEffect, useRef, useCallback } from 'react';
import { isCwpUserSignedIn } from '../../../../../../_helpers/isCwpUserSignedIn';

type TokenIframeProps = {
	iframeSrc: string; // URL to load in the iframe
};

// Define specific types for custom event details
type TokenStatusDetail = {
	status: 'generated' | 'expired';
	hasToken: boolean;
};
type UserStatusDetail = { status: number };

// Union type for all possible event details
type CustomEventDetail = TokenStatusDetail | UserStatusDetail;

// Custom event types for better type safety
declare global {
	interface WindowEventMap {
		chatblk_tokenStatus: CustomEvent<TokenStatusDetail>;
		chatblk_userStatus: CustomEvent<UserStatusDetail>;
	}
}

const ChatblkTokenIframe: React.FC<TokenIframeProps> = ({ iframeSrc }) => {
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const tokenWatcherRef = useRef<NodeJS.Timeout | null>(null);
	const visibilityHandlerRef = useRef<() => void>();
	const isRefreshingTokenRef = useRef<boolean>(false);

	// Utility function to clear all session storage items
	const clearSessionData = useCallback(() => {
		sessionStorage.removeItem('chatblk_access_token');
		sessionStorage.removeItem('chatblk_iframe_loaded');
		sessionStorage.removeItem('chatblk_user_status');
	}, []);

	// Utility function to dispatch custom events
	const dispatchCustomEvent = useCallback(
		(eventName: string, detail: CustomEventDetail) => {
			const event = new CustomEvent(eventName, { detail });
			window.dispatchEvent(event);
		},
		[]
	);

	// Utility function to set iframe src safely
	const setIframeSrc = useCallback((src: string) => {
		if (iframeRef.current) {
			iframeRef.current.src = src;
			if (src) {
				sessionStorage.setItem('chatblk_iframe_loaded', 'true');
			} else {
				sessionStorage.removeItem('chatblk_iframe_loaded');
			}
		}
	}, []);

	// Utility function to refresh token by reloading iframe
	const refreshToken = useCallback(() => {
		if (isRefreshingTokenRef.current) {
			return;
		}

		isRefreshingTokenRef.current = true;

		// Clear the iframe loaded flag and reload
		sessionStorage.removeItem('chatblk_iframe_loaded');
		setIframeSrc(''); // Clear first

		// Small delay to ensure iframe is cleared before reloading
		setTimeout(() => {
			setIframeSrc(iframeSrc);
			// Reset the refreshing flag after iframe loads
			setTimeout(() => {
				isRefreshingTokenRef.current = false;
			}, 2000);
		}, 100);
	}, [iframeSrc, setIframeSrc]);

	// Token validation logic
	const validateAndManageToken = useCallback(() => {
		const isSignedIn = isCwpUserSignedIn();
		const token = sessionStorage.getItem('chatblk_access_token');
		const iframeLoaded = sessionStorage.getItem('chatblk_iframe_loaded');

		// Handle user not signed in
		if (!isSignedIn) {
			setIframeSrc(''); // Set iframe src to empty
			clearSessionData();
			isRefreshingTokenRef.current = false;
			return;
		}

		// Handle signed in user without token - load iframe for first time
		if (!token && iframeLoaded !== 'true' && !isRefreshingTokenRef.current) {
			setIframeSrc(iframeSrc);
			return;
		}

		// Handle signed in user with token - validate token
		if (token) {
			try {
				const parsedToken = JSON.parse(token);
				const currentTime = Math.floor(Date.now() / 1000);
				const tokenCreatedAt = Math.floor(parsedToken.created_at / 1000);
				const tokenExpiresIn = parsedToken.expires_in;
				const tokenExpiryTime = tokenCreatedAt + tokenExpiresIn;
				const timeUntilExpiry = tokenExpiryTime - currentTime;

				if (timeUntilExpiry <= 0) {
					// Token expired
					dispatchCustomEvent('chatblk_tokenStatus', {
						status: 'expired',
						hasToken: false,
					});
					refreshToken();
				} else if (timeUntilExpiry <= 300) {
					// Token expiring soon (within 5 minutes) - proactively refresh
					refreshToken();
				}
			} catch (error) {
				console.error('Error parsing chatblk_access_token:', error);
				// Clear invalid token and refresh
				clearSessionData();
				refreshToken();
			}
		}
	}, [
		iframeSrc,
		setIframeSrc,
		clearSessionData,
		dispatchCustomEvent,
		refreshToken,
	]);

	// Handle page visibility change (when user returns to tab after long time)
	const handleVisibilityChange = useCallback(() => {
		if (!document.hidden) {
			validateAndManageToken();
		}
	}, [validateAndManageToken]);

	// Handle iframe load and check status
	const handleIframeLoad = useCallback(() => {
		try {
			const iframeWindow = iframeRef.current?.contentWindow;

			if (iframeWindow) {
				// Request status check from iframe
				iframeWindow.postMessage({ type: 'CHECK_STATUS' }, '*');
			}
		} catch (error) {
			console.error('Error handling iframe load:', error);
		}
	}, []);

	// Handle messages from iframe
	const handleMessage = useCallback(
		(event: MessageEvent) => {
			if (event.data.type === 'STATUS_RESPONSE') {
				const { status } = event.data;

				if (status === 401) {
					sessionStorage.setItem('chatblk_user_status', 'unauthorized_user');
					console.log('User is an unverified FP user.');
					dispatchCustomEvent('chatblk_userStatus', { status: 401 });
				} else if (status === 200) {
					sessionStorage.setItem('chatblk_user_status', 'authorized_user');
					dispatchCustomEvent('chatblk_userStatus', { status: 200 });
					console.log('User is a verified FP user.');
				} else {
					// Handle generic status
					sessionStorage.setItem(
						'chatblk_user_status',
						`generic_status_${status}`
					);
					console.log(`Received generic status: ${status}`);
					dispatchCustomEvent('chatblk_userStatus', { status });
				}
			}
		},
		[dispatchCustomEvent]
	);

	// Handle session storage changes (for cross-tab communication and manual token changes)
	const handleStorageChange = useCallback(
		(event: StorageEvent) => {
			if (event.key === 'chatblk_access_token') {
				if (event.newValue) {
					// Token was added/updated
					console.log('Token updated in session storage.');
					dispatchCustomEvent('chatblk_tokenStatus', {
						status: 'generated',
						hasToken: true,
					});
					// Validate the new/updated token
					setTimeout(() => validateAndManageToken(), 100);
				} else {
					// Token was removed
					console.log('Token removed from session storage.');
					dispatchCustomEvent('chatblk_tokenStatus', {
						status: 'expired',
						hasToken: false,
					});
				}
			}
		},
		[dispatchCustomEvent, validateAndManageToken]
	);

	// Main useEffect - combines all initialization and cleanup
	useEffect(() => {
		// Clear iframe loaded flag on mount for fresh start
		sessionStorage.removeItem('chatblk_iframe_loaded');
		isRefreshingTokenRef.current = false;

		// Initial token validation
		validateAndManageToken();

		// Set up token watcher (check every 60 seconds for more responsive token management)
		tokenWatcherRef.current = setInterval(validateAndManageToken, 60000);

		// Set up page visibility listener
		visibilityHandlerRef.current = handleVisibilityChange;
		document.addEventListener('visibilitychange', handleVisibilityChange);

		// Set up message listener for iframe communication
		window.addEventListener('message', handleMessage);

		// Set up storage listener for cross-tab communication
		window.addEventListener('storage', handleStorageChange);

		// Add focus listener to check token when window gains focus
		const handleFocus = () => {
			console.log('Window gained focus. Checking token validity.');
			validateAndManageToken();
		};
		window.addEventListener('focus', handleFocus);

		// Cleanup function
		return () => {
			// Clear token watcher
			if (tokenWatcherRef.current) {
				clearInterval(tokenWatcherRef.current);
			}

			// Remove event listeners
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			window.removeEventListener('message', handleMessage);
			window.removeEventListener('storage', handleStorageChange);
			window.removeEventListener('focus', handleFocus);
		};
	}, [
		iframeSrc,
		validateAndManageToken,
		handleVisibilityChange,
		handleMessage,
		handleStorageChange,
	]);

	return (
		<iframe
			ref={iframeRef}
			src="" // Initially empty
			style={{ display: 'none' }}
			aria-hidden="true"
			tabIndex={-1}
			id="chatblk-iframe"
			onLoad={handleIframeLoad}
		/>
	);
};

export default ChatblkTokenIframe;
